import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  FiArrowRight,
  FiBriefcase,
  FiGrid,
  FiLoader,
  FiLogOut,
  FiSearch,
  FiShield,
  FiX,
} from "react-icons/fi";
import "@fontsource/montserrat/400.css";
import "@fontsource/montserrat/500.css";
import "@fontsource/montserrat/600.css";
import pilcoreLogo from "../assets/Logo_Pilcore_Vertical_Blanco.svg";
import "./WorkspaceSelector.css";

const WORKSPACE_COPY = {
  en: {
    eyebrow: "Your Pilcore workspace",
    title: "Choose your workspace",
    subtitle: "Select a business unit to access its modules and daily operations.",
    businessUnits: "Business units",
    available: (count) => `${count} available`,
    search: "Search business units",
    searchPlaceholder: "Search by name or code…",
    clearSearch: "Clear search",
    unit: "Business unit",
    openUnit: (name) => `Open ${name}`,
    enter: "Enter workspace",
    switching: "Opening workspace…",
    loading: "Loading your workspaces…",
    emptyTitle: "No business units available",
    emptyDescription: "Contact your administrator if you need access to a business unit.",
    noMatches: "No business units match your search",
    tryAnother: "Try a different name or code, or clear the search.",
    errorTitle: "We could not complete workspace access",
    errorDescription: "Please try again in a moment.",
    retry: "Try again",
    globalTitle: "Other workspaces",
    globalDescription: "Organization-wide access",
    admin: "Admin Panel",
    adminDescription: "Manage the platform and its configuration.",
    corporate: "Corporate access",
    corporateDescription: "Shared tenders, catalog and suppliers.",
    signOut: "Sign out",
  },
  es: {
    eyebrow: "Tu espacio de trabajo Pilcore",
    title: "Elige tu espacio de trabajo",
    subtitle: "Selecciona una unidad de negocio para acceder a sus módulos y operativa diaria.",
    businessUnits: "Unidades de negocio",
    available: (count) => `${count} disponible${count === 1 ? "" : "s"}`,
    search: "Buscar unidades de negocio",
    searchPlaceholder: "Buscar por nombre o código…",
    clearSearch: "Borrar búsqueda",
    unit: "Unidad de negocio",
    openUnit: (name) => `Abrir ${name}`,
    enter: "Entrar al espacio",
    switching: "Abriendo espacio de trabajo…",
    loading: "Cargando tus espacios de trabajo…",
    emptyTitle: "No hay unidades de negocio disponibles",
    emptyDescription: "Contacta con tu administrador si necesitas acceso a una unidad de negocio.",
    noMatches: "Ninguna unidad coincide con tu búsqueda",
    tryAnother: "Prueba con otro nombre o código, o borra la búsqueda.",
    errorTitle: "No hemos podido completar el acceso",
    errorDescription: "Vuelve a intentarlo en unos instantes.",
    retry: "Reintentar",
    globalTitle: "Otros espacios de trabajo",
    globalDescription: "Acceso a toda la organización",
    admin: "Panel de administración",
    adminDescription: "Gestiona la plataforma y su configuración.",
    corporate: "Acceso corporativo",
    corporateDescription: "Licitaciones, catálogo y proveedores compartidos.",
    signOut: "Cerrar sesión",
  },
};

const normalizeSearch = (value) => String(value ?? "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLocaleLowerCase();

export default function WorkspaceSelector({
  businessUnits = [],
  loading = false,
  error = null,
  switchingId = null,
  canAccessAdmin = false,
  canAccessCorporate = false,
  languageId = "en",
  themeId = "light",
  onSelectBusinessUnit,
  onAdmin,
  onCorporate,
  onSignOut,
  onRetry,
}) {
  const [query, setQuery] = useState("");
  const componentId = useId();
  const headingRef = useRef(null);
  const copy = WORKSPACE_COPY[languageId] || WORKSPACE_COPY.en;
  const hasSearch = businessUnits.length > 4;
  const isSwitching = switchingId !== null && switchingId !== undefined;
  const normalizedQuery = hasSearch ? normalizeSearch(query).trim() : "";
  const visibleBusinessUnits = useMemo(() => businessUnits.filter((unit) => (
    !normalizedQuery || [unit.name, unit.code, unit.id].some((value) => (
      normalizeSearch(value).includes(normalizedQuery)
    ))
  )), [businessUnits, normalizedQuery]);
  const hasGlobalAccess = canAccessAdmin || canAccessCorporate;
  const errorMessage = typeof error === "string" ? error : copy.errorDescription;

  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true });
  }, []);

  const renderError = () => (
    <div className="workspace-selector__error" role="alert">
      <div>
        <strong>{copy.errorTitle}</strong>
        <p>{errorMessage}</p>
      </div>
      {onRetry && (
        <button type="button" onClick={onRetry} disabled={isSwitching}>
          {copy.retry}
        </button>
      )}
    </div>
  );

  return (
    <main className="workspace-selector" data-theme={themeId} aria-labelledby={`${componentId}-title`}>
      <div className="workspace-selector__shell">
        <header className="workspace-selector__header">
          <div className="workspace-selector__brand">
            <img src={pilcoreLogo} alt="Pilcore" />
          </div>
          <div className="workspace-selector__heading">
            <p className="workspace-selector__eyebrow">{copy.eyebrow}</p>
            <h1 id={`${componentId}-title`} ref={headingRef} tabIndex={-1}>{copy.title}</h1>
            <p className="workspace-selector__subtitle">{copy.subtitle}</p>
          </div>
          {onSignOut && (
            <button
              type="button"
              className="workspace-selector__sign-out"
              onClick={onSignOut}
              disabled={isSwitching}
              aria-label={copy.signOut}
              title={copy.signOut}
            >
              <FiLogOut aria-hidden="true" />
              <span>{copy.signOut}</span>
            </button>
          )}
        </header>

        <section className="workspace-selector__units" aria-labelledby={`${componentId}-units-heading`}>
          <div className="workspace-selector__toolbar">
            <div className="workspace-selector__section-heading">
              <h2 id={`${componentId}-units-heading`}>{copy.businessUnits}</h2>
              {!loading && <span>{copy.available(businessUnits.length)}</span>}
            </div>
            {hasSearch && (
              <div className="workspace-selector__search">
                <FiSearch aria-hidden="true" />
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={copy.searchPlaceholder}
                  aria-label={copy.search}
                  aria-controls={`${componentId}-unit-list`}
                  disabled={isSwitching}
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    aria-label={copy.clearSearch}
                    disabled={isSwitching}
                  >
                    <FiX aria-hidden="true" />
                  </button>
                )}
              </div>
            )}
          </div>

          <div
            className="workspace-selector__unit-scroll"
            id={`${componentId}-unit-list`}
            aria-busy={loading || isSwitching}
          >
            {loading ? (
              <div className="workspace-selector__state" role="status">
                <FiLoader className="workspace-selector__spinner" aria-hidden="true" />
                <p>{copy.loading}</p>
              </div>
            ) : (
              <>
                {error && renderError()}
                {businessUnits.length === 0 ? (
                  !error && (
                    <div className="workspace-selector__state" role="status">
                      <FiGrid aria-hidden="true" />
                      <h3>{copy.emptyTitle}</h3>
                      <p>{copy.emptyDescription}</p>
                    </div>
                  )
                ) : visibleBusinessUnits.length === 0 ? (
                  <div className="workspace-selector__state" role="status">
                    <FiSearch aria-hidden="true" />
                    <h3>{copy.noMatches}</h3>
                    <p>{copy.tryAnother}</p>
                    <button type="button" onClick={() => setQuery("")} disabled={isSwitching}>
                      {copy.clearSearch}
                    </button>
                  </div>
                ) : (
                  <div className="workspace-selector__grid">
                    {visibleBusinessUnits.map((unit, index) => {
                      const name = unit.name || unit.code || `${copy.unit} ${unit.id ?? index + 1}`;
                      const isSelected = isSwitching && String(switchingId) === String(unit.id);
                      return (
                        <button
                          key={unit.id ?? index}
                          type="button"
                          className={`workspace-selector__unit-card${isSelected ? " is-opening" : ""}`}
                          onClick={() => onSelectBusinessUnit?.(unit)}
                          disabled={isSwitching}
                          aria-label={copy.openUnit(name)}
                          aria-busy={isSelected}
                        >
                          <span className="workspace-selector__card-icon" aria-hidden="true"><FiGrid /></span>
                          <span className="workspace-selector__card-content">
                            <span className="workspace-selector__card-kicker">{unit.code || copy.unit}</span>
                            <span className="workspace-selector__card-title">{name}</span>
                          </span>
                          <span className="workspace-selector__card-action" aria-hidden="true">
                            <span>{isSelected ? copy.switching : copy.enter}</span>
                            {isSelected ? <FiLoader className="workspace-selector__spinner" /> : <FiArrowRight />}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
          <span className="workspace-selector__sr-only" role="status" aria-live="polite">
            {isSwitching ? copy.switching : hasSearch && query ? copy.available(visibleBusinessUnits.length) : ""}
          </span>
        </section>

        {hasGlobalAccess && (
          <section className="workspace-selector__global" aria-labelledby={`${componentId}-global-heading`}>
            <div className="workspace-selector__section-heading">
              <h2 id={`${componentId}-global-heading`}>{copy.globalTitle}</h2>
              <span>{copy.globalDescription}</span>
            </div>
            <div className="workspace-selector__global-grid">
              {canAccessAdmin && (
                <button type="button" className="workspace-selector__global-card" onClick={onAdmin} disabled={isSwitching}>
                  <span className="workspace-selector__global-icon" aria-hidden="true"><FiShield /></span>
                  <span className="workspace-selector__card-content">
                    <span className="workspace-selector__global-title">{copy.admin}</span>
                    <span className="workspace-selector__global-description">{copy.adminDescription}</span>
                  </span>
                  <FiArrowRight aria-hidden="true" />
                </button>
              )}
              {canAccessCorporate && (
                <button type="button" className="workspace-selector__global-card" onClick={onCorporate} disabled={isSwitching}>
                  <span className="workspace-selector__global-icon" aria-hidden="true"><FiBriefcase /></span>
                  <span className="workspace-selector__card-content">
                    <span className="workspace-selector__global-title">{copy.corporate}</span>
                    <span className="workspace-selector__global-description">{copy.corporateDescription}</span>
                  </span>
                  <FiArrowRight aria-hidden="true" />
                </button>
              )}
            </div>
          </section>
        )}
      </div>
    </main>
  );
}
